import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, switchMap, takeWhile, timer } from 'rxjs';

import { environment } from '../../../../environments/environment';

import {
  CashflowAdvisorNotes,
  CashflowAnalysisOverview,
  CashflowAnalysisOverviewParams,
  CashflowAnalysisRequest,
  CashflowAnalysisResult,
  CashflowMessageRequest,
} from '../../model';

/** Objektive Finanzanalyse of the Cashflow tab — AI findings from the ai service (FIN-7165). */
@Injectable({ providedIn: 'root' })
export class CashflowAnalysisService {
  constructor(private readonly http: HttpClient) {}

  /** Starts a run (202 with a RUNNING handle). Costs AI tokens; 429 when the wallet is empty. */
  public runForClient(clientId: string, request: CashflowAnalysisRequest): Observable<CashflowAnalysisResult> {
    return this.http.post<CashflowAnalysisResult>(
      `${environment.apiEndpoint}/ai/cashflow-analysis/clients/${clientId}`,
      request,
    );
  }

  /** Latest run in any status; 404 when the client was never analysed. */
  public getLatest(clientId: string): Observable<CashflowAnalysisResult> {
    return this.http.get<CashflowAnalysisResult>(
      `${environment.apiEndpoint}/ai/cashflow-analysis/clients/${clientId}/latest`,
    );
  }

  /** Sends a chat message from the advisor to the client; marks the finding as sent when findingIndex is set. */
  public sendClientMessage(clientId: string, request: CashflowMessageRequest): Observable<CashflowAnalysisResult> {
    return this.http.post<CashflowAnalysisResult>(
      `${environment.apiEndpoint}/ai/cashflow-analysis/clients/${clientId}/messages`,
      request,
    );
  }

  /** The advisor's stored "Analyse anpassen" entries for the client (empty list when none). */
  public getAdvisorNotes(clientId: string): Observable<CashflowAdvisorNotes> {
    return this.http.get<CashflowAdvisorNotes>(
      `${environment.apiEndpoint}/ai/cashflow-analysis/clients/${clientId}/notes`,
    );
  }

  /** Replaces the stored entries. */
  public putAdvisorNotes(clientId: string, notes: string[]): Observable<CashflowAdvisorNotes> {
    return this.http.put<CashflowAdvisorNotes>(
      `${environment.apiEndpoint}/ai/cashflow-analysis/clients/${clientId}/notes`,
      { notes },
    );
  }

  /** Polls the latest run every `intervalMs` until it leaves RUNNING/PENDING (last emission is terminal). */
  public pollLatest(clientId: string, intervalMs: number = 4000): Observable<CashflowAnalysisResult> {
    return timer(intervalMs, intervalMs).pipe(
      switchMap(() => this.getLatest(clientId)),
      takeWhile((a: CashflowAnalysisResult): boolean => a.status === 'RUNNING' || a.status === 'PENDING', true),
    );
  }

  public getOverview(params: CashflowAnalysisOverviewParams): Observable<CashflowAnalysisOverview> {
    let httpParams: HttpParams = new HttpParams().set('page', params.page).set('page_size', params.pageSize);

    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }

    if (params.status) {
      httpParams = httpParams.set('status', params.status);
    }

    return this.http.get<CashflowAnalysisOverview>(`${environment.apiEndpoint}/ai/cashflow-analysis/overview`, {
      params: httpParams,
    });
  }
}
